/// <reference path="./types/aem-headless-client-js.d.ts" />
import AEMHeadless from '@adobe/aem-headless-client-js';

import {createAssetPathResolver} from './assetPath';
import {CONTENT_SOURCE, getContentSource} from './contentSource';
import type {AemClient, AemClientConfig} from './types';

const S3_VERSION = 'v1';
const AEM_VERSION = 'v1';
const AEM_HOST = import.meta.env.VITE_AEM_HOST || '';
const AEM_AUTH_COOKIE_NAME = import.meta.env.VITE_AEM_AUTH_COOKIE_NAME || '';
const AEM_AUTH_COOKIE_VALUE = import.meta.env.VITE_AEM_AUTH_COOKIE_VALUE || '';

type AemResponse<TItem> = {
    data?: Record<string, {item?: TItem} | undefined>;
};

type ContentRequest = {
    queryName: string;
    marketName: string;
    /** Path under the market's global folder, without the DAM prefix. */
    damSuffix: string;
    /** Path under the market's S3 folder. */
    s3Suffix: string;
};

let headlessClient: AEMHeadless | null = null;

const getHeadlessClient = (): AEMHeadless => {
    headlessClient ??= new AEMHeadless({});
    return headlessClient;
};

const ensureAuthCookie = () => {
    if (!AEM_AUTH_COOKIE_NAME || document.cookie.includes(AEM_AUTH_COOKIE_NAME)) return;

    // biome-ignore lint/suspicious/noDocumentCookie: no alternative API for setting cookies
    document.cookie = `${AEM_AUTH_COOKIE_NAME}=${AEM_AUTH_COOKIE_VALUE}; path=/; domain=.iqos.com; secure; SameSite=None`;
};

const getAemEnv = () => (import.meta.env.VITE_ENV === 'local' ? 'dev' : import.meta.env.VITE_ENV);

const getJson = async (url: string, withCredentials = false): Promise<unknown> => {
    const response = await fetch(url, withCredentials ? {credentials: 'include'} : undefined);
    if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText}`.trim());

    const body = await response.text();

    try {
        return JSON.parse(body);
    } catch {
        // A non-JSON body must surface as the caller's domain error, not as a parse error.
        return undefined;
    }
};

const unwrapItem = <TItem>(rawResponse: unknown): TItem | undefined => {
    if (!rawResponse || typeof rawResponse !== 'object') return undefined;

    const data = (rawResponse as AemResponse<TItem>).data ?? {};
    const mainKey = Object.keys(data)[0];

    return mainKey ? data[mainKey]?.item : undefined;
};

export const createAemClient = ({graphqlEndpoint, damPath, localAssetsHost}: AemClientConfig): AemClient => {
    /** Remote AEM publish endpoint (/wam-consumer). */
    const fetchViaEndpoint = (queryName: string, path: string) => {
        ensureAuthCookie();

        const url = `${AEM_HOST}/wam-consumer/graphql/execute.json/${graphqlEndpoint}/${queryName};path=${path}`;
        return getJson(url, true);
    };

    /** Local AEM SDK: AEMHeadless client through the /graphql proxy. */
    const fetchViaAemSdk = (queryName: string, path: string) =>
        getHeadlessClient().runPersistedQuery(`${graphqlEndpoint}/${queryName}`, {path});

    const fetchItem = async <TItem>({queryName, marketName, damSuffix, s3Suffix}: ContentRequest) => {
        const source = getContentSource();

        if (source === CONTENT_SOURCE.S3) {
            return unwrapItem<TItem>(await getJson(`/aemcontent/${marketName}/${S3_VERSION}/${s3Suffix}`));
        }

        const path = `/content/dam/${damPath}/${marketName}/${AEM_VERSION}/${getAemEnv()}/global/${damSuffix}`;
        const result =
            source === CONTENT_SOURCE.AEM_CLOUD
                ? await fetchViaEndpoint(queryName, path)
                : await fetchViaAemSdk(queryName, path);

        return unwrapItem<TItem>(result);
    };

    const initMarketConfig = async <TConfig = unknown>(marketName: string): Promise<TConfig> => {
        try {
            const config = await fetchItem<TConfig>({
                queryName: 'get-market-config',
                marketName,
                damSuffix: 'market-config/market-config',
                s3Suffix: 'configs/config.json',
            });

            if (!config) throw new Error(`Market config is not initialized for market: ${marketName}`);

            return config;
        } catch (error) {
            console.error('initMarketConfig error:', error);
            throw error;
        }
    };

    const initMarketContent = async <TContent = unknown>(
        marketName: string,
        languageCode: string
    ): Promise<TContent> => {
        try {
            const content = await fetchItem<TContent>({
                queryName: 'get-market-content',
                marketName,
                damSuffix: `${languageCode}/market-content`,
                s3Suffix: `${languageCode}/content.json`,
            });

            if (!content) {
                throw new Error(
                    `Market content is not initialized for market: ${marketName} and language: ${languageCode}`
                );
            }

            return content;
        } catch (error) {
            console.error('initMarketContent error:', error);
            throw error;
        }
    };

    return {initMarketConfig, initMarketContent, getAssetPath: createAssetPathResolver(localAssetsHost)};
};
